import { useQuery } from '@apollo/client';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { Checkbox, ITag } from '@fluentui/react';
import {
  Button,
  FluentProvider,
  webLightTheme,
  Field,
  Input,
  Select,
  Tooltip,
} from '@fluentui/react-components';
import { InfoRegular } from '@fluentui/react-icons';
import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { Controller, DeepPartial, useForm, useWatch } from 'react-hook-form';
import { PROCESS_TYPE_WITH_TEMPLATES_LIST_QUERY } from '../../../../utils/statApi/ProcessTypesApi';
import IDropdownReference from '../../../../utils/types/IDropdownReference';
import IProcessConfig from '../../../../utils/types/IProcessConfig';
import LoadingErrorMessage from '../../../common/errorContent/LoadingErrorMessage';
import QueryBasedDropdown from '../../../common/formFields/queryBasedDropdown/QueryBasedDropdown';
import UtcDatePicker from '../../../common/utcDatePicker/UtcDatePicker';
import {
  filingDueDateDefaultDate,
  recurrenceOptions,
  reportingPeriodEndDateDefaultDate,
} from './CreateProcessConfigUtils';
import ProcessTemplateDropdown from './ProcessTemplateDropdown';

import InitialProcessDates from './InitialProcessDates';
import { ProcessType } from '../../processes/Interfaces';
import { GET_PROCESS_CONFIG_COMPANY_DEFAULT_DATES } from '../../../../utils/statApi/ProcessConfigApi';
import hasAccess from '../../../../utils/authorization/authorizationCheck';
import UserContext from '../../../../utils/authorization/UserContext';
import { StatUser } from '../../../../utils/statApi/UsersApi';
import ComplianceRequirementPicker from '../../../countries/ComplianceRequirementPicker';
import { IComplianceRequirement } from '../../../countries/CountryConfiguration.config';
import FeatureFlagged from '../../../common/featureFlagged/featureFlagged';
import { iconButtonStyles } from '../../../../app/common/styles/CommonStyleObjects';

interface ICreateProcessConfigDetailsFormProps {
  onValidated: (isValid: boolean) => void;
  setData: Dispatch<SetStateAction<IProcessConfig>>;
  data: IProcessConfig;
  onTemplateChange: (newTemplateId: number) => void;
  companyCode: string;
}

export const offsetDateIconStyles = {
  marginLeft: 5,
};

const dropdownStyles = { width: 300 };

const CreateProcessConfigDetailsForm = (
  props: ICreateProcessConfigDetailsFormProps,
): JSX.Element => {
  const { data, setData, onValidated, onTemplateChange, companyCode } = props;
  const [selectedComplianceRequirement, setSelectedComplianceRequirement] = useState(null);
  const user: StatUser = useContext(UserContext);
  const [reportingPeriodYear, setReportingPeriodYear] = useState(data.reportingPeriodYear);
  const [disableUseLastDayOfMonth, setDisableUseLastDayOfMonth] = useState(
    data.recurrence === 'ONETIME' || data.recurrence === 'WEEKLY',
  );

  const form = useForm<IProcessConfig>({
    reValidateMode: 'onChange',
    mode: 'onChange',
    defaultValues: data as IProcessConfig,
  });

  const { errors, setValue, control, formState, getValues, trigger } = form;
  const { isValid, dirtyFields } = formState;

  const {
    loading: loadingProcessType,
    data: dataProcessType,
    error: errorProcessTypeQuery,
  } = useQuery(PROCESS_TYPE_WITH_TEMPLATES_LIST_QUERY);

  const { data: dataDefaultDates, loading: loadingDefaultDates } = useQuery(
    GET_PROCESS_CONFIG_COMPANY_DEFAULT_DATES,
    {
      fetchPolicy: 'no-cache',
      variables: { companyCode },
    },
  );

  const defaultDates = {
    processConfigCompanyDefaultDates: {
      statutoryYearEnd: null as string,
      auditFilingDueDate: null as string,
      hasDefaultCompanyAFSTemplate: false,
    },
  };

  const { processConfigCompanyDefaultDates } =
    !loadingDefaultDates && dataDefaultDates ? dataDefaultDates : defaultDates;

  const { hasDefaultCompanyAFSTemplate, statutoryYearEnd, auditFilingDueDate } =
    processConfigCompanyDefaultDates;

  const watchForm: DeepPartial<IProcessConfig> = useWatch({
    control,
    defaultValue: data as IProcessConfig,
  });

  const isAnnualFinancialStatements =
    getValues('processType')?.fieldName === ProcessType.AnnualFinancialStatements;

  const { permissions } = user;
  const hasAdminAccessToChangeDates =
    hasAccess(permissions, 'ProcessConfiguration:EditReportingPeriodEndDateAFS') &&
    hasAccess(permissions, 'ProcessConfiguration:EditFilingDueDateAFS');
  const { processTypesWithTemplates } =
    !loadingProcessType && dataProcessType ? dataProcessType : [];
  useEffect(() => {
    const reportingPeriodEndDate = getValues('reportingPeriodEndDate');
    const filingDueDate = getValues('filingDueDate');

    if (
      isAnnualFinancialStatements &&
      auditFilingDueDate &&
      statutoryYearEnd &&
      !reportingPeriodEndDate &&
      !filingDueDate
    ) {
      setValue('reportingPeriodEndDate', reportingPeriodEndDateDefaultDate(statutoryYearEnd), {
        shouldDirty: true,
      });
      setValue('filingDueDate', filingDueDateDefaultDate(auditFilingDueDate, statutoryYearEnd), {
        shouldDirty: true,
      });
      trigger();
    }
    onValidated(isValid);

    if (isValid) {
      setData({
        ...data,
        name: getValues('name'),
        processType: getValues('processType'),
        processTemplate: getValues('processTemplate'),
        recurrence: getValues('recurrence'),
        filingDueDate: getValues('filingDueDate'),
        reportingPeriodEndDate: getValues('reportingPeriodEndDate'),
        recurrenceEndDate: getValues('recurrenceEndDate'),
        useLastDayOfMonth: getValues('useLastDayOfMonth'),
        reportingPeriodYear,
        complianceRequirement: getValues('complianceRequirement'),
      });
    }
  }, [isValid, watchForm, reportingPeriodYear, dataDefaultDates]);

  const onComplianceRequirementUpdate = (items: ITag[]): void => {
    const complianceRequirement = items.map((item) => {
      const intId = typeof item.key === 'string' ? parseInt(item.key, 10) : item.key;
      const newObject: Partial<IComplianceRequirement> = {
        id: intId,
        name: item.name,
      };
      return newObject;
    });

    setSelectedComplianceRequirement(complianceRequirement);

    setValue('complianceRequirement', complianceRequirement[0], { shouldDirty: true });
  };

  return (
    <FluentProvider theme={webLightTheme}>
      <Stack tokens={{ childrenGap: 10 }}>
        <FeatureFlagged flagName="CountryConfiguration">
          <Stack horizontal>
            <Controller
              name="complianceRequirement"
              control={control}
              defaultValue={[data?.complianceRequirement]}
              render={(): React.ReactElement => (
                <ComplianceRequirementPicker
                  selectedItem={selectedComplianceRequirement}
                  setSelectedItem={onComplianceRequirementUpdate}
                />
              )}
            />
            <Tooltip
              withArrow
              content={<span>Compliance Requirement that this process satisfies</span>}
              relationship="label"
            >
              <FluentProvider theme={webLightTheme}>
                <Button
                  appearance="subtle"
                  aria-label="Compliance Requirement Info"
                  icon={<InfoRegular color={iconButtonStyles.color} />}
                  style={offsetDateIconStyles}
                />
              </FluentProvider>
            </Tooltip>
          </Stack>
        </FeatureFlagged>

        <Controller
          name="processType"
          label="Process Type"
          valueKey="processType"
          control={control}
          render={({ onChange }): React.ReactElement => (
            <>
              <LoadingErrorMessage
                loading={loadingProcessType}
                error={errorProcessTypeQuery}
                actionName="loading the process type"
              />
              <QueryBasedDropdown
                label="Process Type"
                value={getValues('processType')}
                valueKey="processType"
                dropdownOptionsData={processTypesWithTemplates}
                handleChange={(newValue: IDropdownReference, valueKey: string): void => {
                  setValue('processType', newValue, { shouldDirty: true });
                  setValue('processTemplate', null);
                  setValue('reportingPeriodEndDate', null, { shouldDirty: true });
                  setValue('filingDueDate', null, { shouldDirty: true });
                  setValue('name', '', { shouldDirty: true });
                  trigger();
                  const resetYear = new Date().getFullYear();
                  setReportingPeriodYear(resetYear);
                }}
                width="100%"
                errors={dirtyFields?.processType && errors}
                required
              />
            </>
          )}
          rules={{
            required: 'Please select a process type',
          }}
        />
        <Controller
          name="processTemplate"
          label="Process Template"
          valueKey="processTemplate"
          control={control}
          render={(): React.ReactElement => (
            <ProcessTemplateDropdown
              processTypeId={getValues('processType')?.id}
              selectedKey={getValues('processTemplate')}
              handleChange={(newValue: IDropdownReference) => {
                setValue('name', newValue.fieldName, { shouldDirty: true });
                onTemplateChange(newValue.id);
                setValue('processTemplate', newValue, { shouldDirty: true });
                trigger();
              }}
              errors={dirtyFields?.processTemplate && errors}
            />
          )}
          rules={{
            required: 'Please select a process template',
          }}
        />
        <Controller
          name="name"
          control={control}
          rules={{
            required: 'Name is required',
            maxLength: {
              value: 200,
              message: 'Input is greater than the maximum character limit of 200.',
            },
          }}
          render={({ onChange, onBlur, value, name, ref }) => (
            <Field
              label="Name"
              required
              validationMessage={dirtyFields?.name && errors?.name?.message}
            >
              <Input
                onChange={onChange}
                onBlur={onBlur}
                value={value}
                name={name}
                ref={ref}
                id="name"
              />
            </Field>
          )}
        />
        <InitialProcessDates
          companyCode={companyCode}
          showDateRangeSelector={
            !hasAdminAccessToChangeDates &&
            !hasDefaultCompanyAFSTemplate &&
            isAnnualFinancialStatements
          }
          reportingPeriodYear={reportingPeriodYear}
          filingDueDate={getValues('filingDueDate')}
          reportingPeriodEndDate={getValues('reportingPeriodEndDate')}
          auditFilingDueDate={auditFilingDueDate}
          statutoryYearEnd={statutoryYearEnd}
          setFilingDueDate={(newValue: string) => {
            setValue('filingDueDate', newValue, { shouldDirty: true });
            trigger();
          }}
          setReportingPeriodEndDate={(newValue: string) => {
            setValue('reportingPeriodEndDate', newValue, { shouldDirty: true });
            trigger();
          }}
          setReportingPeriodYear={setReportingPeriodYear}
          control={control}
          dirtyFields={dirtyFields}
          errors={errors}
          isAnnualFinancialStatements={isAnnualFinancialStatements}
        />
        <Controller
          name="useLastDayOfMonth"
          control={control}
          defaultValue={data?.useLastDayOfMonth}
          render={({ onBlur, value, name }): React.ReactElement => (
            <Checkbox
              label="Use Last Day of Month"
              onChange={(
                e: React.FormEvent<HTMLInputElement | HTMLElement>,
                checked: boolean,
              ): void => {
                setValue('useLastDayOfMonth', checked, { shouldDirty: true });
                trigger();
              }}
              checked={value}
              name={name}
              disabled={disableUseLastDayOfMonth}
            />
          )}
        />
        <Controller
          name="recurrence"
          control={control}
          defaultValue={data?.recurrence}
          value={getValues('recurrence')}
          render={({ value }): React.ReactElement => (
            <Field
              label="Recurrence"
              validationMessage={dirtyFields?.recurrence && errors?.recurrence?.message}
            >
              <Select
                onChange={(event, option): void => {
                  if (option.value === 'WEEKLY' || option.value === 'ONETIME') {
                    setValue('useLastDayOfMonth', false, { shouldDirty: true });
                    setDisableUseLastDayOfMonth(true);
                  } else {
                    setDisableUseLastDayOfMonth(false);
                  }
                  setValue('recurrence', option.value, { shouldDirty: true });
                  trigger();
                }}
                defaultValue={value}
                required
                style={dropdownStyles}
                placeholder="Select an option"
              >
                {recurrenceOptions.map((option) => (
                  <option key={option.key.toString()} value={option.key.toString()}>
                    {option.text}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          rules={{
            required: 'Please select a recurrence',
          }}
        />
        <Stack horizontal>
          <Controller
            name="recurrenceEndDate"
            control={control}
            defaultValue={data?.recurrenceEndDate}
            render={({ value }): React.ReactElement => (
              <UtcDatePicker
                label="Deactivate Date"
                value={value}
                onSelectDate={(utcDateString: string): void => {
                  setValue('recurrenceEndDate', utcDateString, { shouldDirty: true });
                  trigger();
                }}
              />
            )}
          />
          <Tooltip
            withArrow
            content={
              <span>
                Date when processes will no longer be generated. Set this value if there is a date
                when processes should no longer be generated.
              </span>
            }
            relationship="label"
          >
            <FluentProvider theme={webLightTheme}>
              <Button
                appearance="subtle"
                aria-label="Deactivate date information"
                icon={<InfoRegular color={iconButtonStyles.color} />}
                style={offsetDateIconStyles}
              />
            </FluentProvider>
          </Tooltip>
        </Stack>
      </Stack>
    </FluentProvider>
  );
};
export default CreateProcessConfigDetailsForm;
